// The date field (spec §8): no time; modes day / month / year. A Persian form shows three lists (year, month name, day)
// of the Jalali calendar, an English one the Gregorian; the value kept is always the stored ISO text
// (YYYY-MM-DD / YYYY-MM / YYYY, with Jalali months stored as the Gregorian month of their 15th).
// Chosen over a calendar pop-up so the field adds no dependency and no bundle weight, works with a keyboard and a screen
// reader as plain selects, and is the same on every device.
import { useState } from "react";
import { h } from "../h.js";
import { slot_props } from "../styles.js";
import { label_of, month_names } from "../messages.js";
import { date_mode_of, calendar_of, date_parts_from_value, value_from_date_parts, days_in_month, year_options } from "../format.js";
import { FieldShell, control_aria } from "./shell.js";

const range = (from, to) => Array.from({ length: to - from + 1 }, (item, index) => from + index);
const to_int = (text) => (text === "" || text === undefined ? undefined : Number.parseInt(text, 10));

// the parts being picked, kept while the date is still incomplete; re-read from the value when it changes from outside
export const DateField = ({ field, id, value, on_change, error, required, disabled, styles, class_names, lang, messages, today }) => {
    const mode = date_mode_of(field);
    const calendar = calendar_of(lang);
    const from_value = date_parts_from_value(value, mode, calendar);
    const [draft, set_draft] = useState({ for_value: value, parts: from_value });
    const parts = draft.for_value === value ? draft.parts : from_value;

    const years = year_options(calendar, field, today);
    // a year outside the window (an old stored value) is still shown
    const shown_years = parts.year !== undefined && !years.includes(parts.year) ? [parts.year, ...years] : years;
    const day_count = days_in_month(calendar, parts.year, parts.month);
    const months = month_names(calendar, lang);

    const pick = (name, text) => {
        const next = { ...parts, [name]: to_int(text) };
        // a day that does not exist in the newly chosen month is dropped
        if (next.day !== undefined && next.day > days_in_month(calendar, next.year, next.month)) {
            next.day = undefined;
        }
        const stored = value_from_date_parts(next, mode, calendar);
        // the value changes only when the date is complete (or becomes empty); the draft keeps the half-picked parts
        if (stored !== (value ?? "")) {
            set_draft({ for_value: stored === "" ? undefined : stored, parts: next });
            on_change(stored === "" ? undefined : stored);
        } else {
            set_draft({ for_value: value, parts: next });
        }
    };

    const select = (name, label, items, current) => h(
        "select",
        {
            key: name,
            ...control_aria({ id: `${id}-${name}`, field, error, required, disabled }),
            id: `${id}-${name}`,
            name: `${id}-${name}`,
            "aria-label": `${field.label} - ${label}`,
            ...slot_props({ slot: "input", class_names, styles, type: "date" }),
            value: current === undefined ? "" : String(current),
            onChange: (event) => pick(name, event.target.value),
        },
        h("option", { value: "" }, label),
        ...items.map((item) => h("option", { key: item.value, value: String(item.value) }, item.title)),
    );

    const selects = [];
    selects.push(select("year", label_of("year", lang, messages), shown_years.map((year) => ({ value: year, title: String(year) })), parts.year));
    if (mode !== "year") {
        selects.push(select("month", label_of("month", lang, messages), months.map((title, index) => ({ value: index + 1, title })), parts.month));
    }
    if (mode === "day") {
        selects.push(select("day", label_of("day", lang, messages), range(1, day_count).map((day) => ({ value: day, title: String(day) })), parts.day));
    }
    // the group is described by the field's hint and error; the first select carries the id the label points at
    return h(
        FieldShell,
        { field, id, error, required, disabled, styles, class_names, as_fieldset: true, variant: "date" },
        h("div", { className: class_names?.date_parts, dir: calendar === "jalali" ? "rtl" : undefined }, ...selects),
    );
};
